import { createContext, useContext, useSyncExternalStore } from 'react'

import type { OnlineMediaApi } from '@/shared/realtime/media/hooks/useOnlineMedia'
import type { OnlineAudioOptions } from '@/shared/realtime/media/livekit/liveKitConfig'

export function createMediaSessionBridge(configure: (options: OnlineAudioOptions | null) => void) {
  let current: OnlineMediaApi | null = null
  const listeners = new Set<() => void>()
  const bridge = {
    configure,
    publish(media: OnlineMediaApi) {
      current = media
      listeners.forEach(listener => listener())
    },
    getSnapshot: () => current,
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    stop() {
      current?.stopAudio()
      current = null
      configure(null)
      listeners.forEach(listener => listener())
    },
  }
  return bridge
}

export const MediaSessionContext = createContext<ReturnType<
  typeof createMediaSessionBridge
> | null>(null)

const subscribeEmpty = () => () => {}
const snapshotEmpty = () => null

export function useMediaSessionSnapshot() {
  const bridge = useContext(MediaSessionContext)
  return useSyncExternalStore(
    bridge?.subscribe ?? subscribeEmpty,
    bridge?.getSnapshot ?? snapshotEmpty,
    snapshotEmpty,
  )
}
